import * as React from 'react';
import {
  useLibrary,
  deleteFile,
  getFiles,
  FileObject,
  FileType,
  GetFilesParams,
} from 'api/library';
import {TransitionGroup, CSSTransition} from 'react-transition-group';
import styled from 'styled-components';
import {lighten} from 'polished';
import Upload from 'components/Upload';
import {InputVariant} from 'components/Input';
import InputSearch from 'components/InputSearch';
import Tabbar, {TabbarProps} from 'components/Tabbar';
import Tab, {TabComponent, TabProps} from 'components/Tab';
import FileCard from 'components/FileCard';
import {IconLoading} from 'icons';
import {Theme} from '../stores/AppStore';

export enum LibraryMode {
  Viewer = 'viewer',
  Picker = 'picker',
}

export interface LibraryProps {
  mode: LibraryMode;
  type?: FileType;
  config?: Config[];
  multiple?: boolean;
  upload?: boolean;
  tabbarProps?: Omit<TabbarProps, 'children'>;
  onSelect?(attached: FileObject[]): void;
}

export type Config = Pick<TabProps,
  | 'name'
  | 'title'
  | 'disabled'> & {
  type?: FileType;
};

export const libraryConfig = [
  {
    name: 'all',
    title: 'Все файлы',
  },
  {
    name: 'images',
    title: 'Изображения',
    type: FileType.Image,
  },
  {
    name: 'videos',
    title: 'Видео',
    type: FileType.Video,
  },
  {
    name: 'documents',
    title: 'Документы',
    type: FileType.Document,
  },
  {
    name: 'links',
    title: 'Ссылки',
    type: FileType.External,
  },
] as Config[];

export const Container = styled.div`
  margin-bottom: 15px;
  text-align: left;
  
  .file-enter {
    opacity: 1;
  }
  .file-enter-active {
    opacity: 1;
    transition: opacity 500s ease-in;
  }
  .file-exit {
    opacity: 1;
  }
  .file-exit-active {
    opacity: 0;
    transition: opacity 500ms ease-in;
  }
  
  @media screen and (min-width: 768px) {
    margin-bottom: 30px;
  }
`;

export const Uploader = styled.header`
  margin: 15px 0;
  
  @media screen and (min-width: 768px) {
    margin: 30px 0;
    padding: 30px 65px;
    border-radius: 5px;
    border: 2px dashed ${({theme}) => theme.name === Theme.Dark ? `
      ${lighten(.2, theme.variables.backgroundColor)};
    ` : (
        theme.variables.borderColor
    )};
  }
`;

export const Search = styled.div`
  margin: 30px 0;
`;

/**
 * This component renders the library
 * @param {LibraryProps} props
 * @return {React.ReactElement}
 * @constructor
 */
export default function Library(
    props: LibraryProps,
): React.ReactElement<React.ReactElement<LibraryProps>> {
  const {status, data} = useLibrary<FileObject[]>();
  const [search, setSearch] = React.useState<string>('');
  const [attached, setAttached] = React.useState<FileObject[]>([]);

  React.useEffect(() => {
    fetchFiles();
  }, []);

  props.onSelect && props.onSelect(attached);

  /**
   * Fetch files and set store
   * @param {GetFilesParams} params
   */
  function fetchFiles(params?: GetFilesParams): void {
    status.setLoaded(false);
    setTimeout(() => {
      getFiles(params).then((response) => {
        if (response && !('statusCode' in response)) {
          data.setFiles(response);
          status.setLoaded(true);
        }
      });
    }, 500);
  }

  /**
   * Handle upload file
   */
  function handleUpload(): void {
    fetchFiles();
  }

  /**
   * Handle input search change
   * @param {React.ChangeEvent<HTMLInputElement>} event
   */
  function handleSearchChange(
      event: React.ChangeEvent<HTMLInputElement>,
  ): void {
    setSearch(event.target.value);
  }

  /**
   * Render tabs from config
   * @return {TabComponent[]}
   */
  function renderTabs(): TabComponent[] {
    const config: Config[] = props.config || libraryConfig;

    /**
     * Render files and filter by type
     * @param {FileType} type
     * @return {JSX.Element[]}
     */
    function renderFiles(type: Config['type']) {
      /**
       * Render the file
       * @param {FileObject} file
       * @return {JSX.Element}
       */
      function renderFile(file: FileObject) {
        /**
         * The handle of select file
         * @param {FileObject} file
         * @return {void}
         */
        function handleSelect(file: FileObject): void {
          const isExist = Boolean(attached.find((attachable) => (
            attachable.id === file.id
          )));
          if (isExist) {
            return setAttached(attached.filter((attachable) => (
              attachable.id !== file.id
            )));
          } else {
            if (props.multiple) {
              return setAttached([
                ...attached,
                file,
              ]);
            } else {
              return setAttached([
                file,
              ]);
            }
          }
        }
        /**
         * The handle of delete file
         * @param {FileObject} id
         * @return {void}
         */
        function handleDelete(id: FileObject['id']): void {
          deleteFile(id).then(() => (
            fetchFiles()
          ));
        }
        return (
          <FileCard
            key={file.id}
            {...file}
            mode={props.mode}
            checked={Boolean(attached.find((attachable) => (
              attachable.id === file.id
            )))}
            onSelect={handleSelect}
            onDelete={handleDelete}
          />
        );
      }
      return data.files?.filter((file) => (
        search.length === 0 ? file : (
          String(file.title)
              .toLowerCase()
              .includes(String(search)
                  .toLowerCase())
        )
      )).filter((file) => (
        type === undefined ? file : (
          file.type === type
        )
      )).map((file) => (
        <CSSTransition key={file.id} timeout={500} classNames="file">
          {renderFile(file)}
        </CSSTransition>
      ));
    }
    return config.map((tab) => (
      <Tab key={tab.name} {...tab}>
        <TransitionGroup>
          {renderFiles(tab.type)}
        </TransitionGroup>
      </Tab>
    ));
  }
  return (
    <Container>
      {props.upload && (
        <Uploader>
          <Upload
            type={props.type}
            onUpload={handleUpload}
          />
        </Uploader>
      )}
      <Search>
        <InputSearch
          type="text"
          value={search}
          onChange={handleSearchChange}
          variant={InputVariant.Accent}
          placeholder="Поиск по файлам"
        />
      </Search>
      <Tabbar {...props.tabbarProps}>
        {renderTabs()}
      </Tabbar>
      {!status.isLoaded && (
        <IconLoading />
      )}
    </Container>
  );
}
